import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { createApiWithPath } from "../../api/client";
import { PeopleConnectionStatus } from "../../api/types/people-connection";
import "./Confirmation.css";

interface ConnectionResponse {
  item: {
    id: string;
    name: string;
    email: string;
    status: PeopleConnectionStatus;
    createdAt: string;
    updatedAt: string;
  };
  message: string;
}

export default function ConnectionConfirmation() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [response, setResponse] = useState<ConnectionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const submitResponse = async () => {
      const value = searchParams.get("value");

      if (!id || !value) {
        setError("Invalid link");
        setLoading(false);
        return;
      }

      try {
        const { data } = await createApiWithPath("v1/connection").post(
          `/${id}/response`,
          { value },
        );
        setResponse(data.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message || "Failed to process your response",
        );
      } finally {
        setLoading(false);
      }
    };

    submitResponse();
  }, [id, searchParams]);

  const updateResponse = async (newValue: "Y" | "N") => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await createApiWithPath("v1/connection").post(
        `/${id}/response`,
        { value: newValue },
      );
      setResponse(data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update your response");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="connection-confirmation">
        <div className="confirmation-card">
          <div className="loading">Processing...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="connection-confirmation">
        <div className="confirmation-card error">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!response) {
    return null;
  }

  const isApproved = response.item.status === PeopleConnectionStatus.APPROVED;

  return (
    <div className="connection-confirmation">
      <div className="confirmation-card">
        <div className={`status-icon ${isApproved ? "approved" : "declined"}`}>
          {isApproved ? "✓" : "✗"}
        </div>

        <h2>{response.message}</h2>

        <div className="details">
          <p>
            <strong>Name:</strong> {response.item.name}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            <span className={`status ${response.item.status}`}>
              {response.item.status}
            </span>
          </p>
          <p className="note">
            {isApproved
              ? "We will send you an introduction email once both parties have confirmed."
              : "Thank you for letting us know. We hope to connect you with someone else in the future."}
          </p>
        </div>

        <div className="actions">
          {!isApproved && (
            <button
              className="btn btn-approve"
              onClick={() => updateResponse("Y")}
              disabled={loading}
            >
              Change to Approved
            </button>
          )}
          {isApproved && (
            <button
              className="btn btn-decline"
              onClick={() => updateResponse("N")}
              disabled={loading}
            >
              Change to Declined
            </button>
          )}
        </div>

        <footer>
          <p>Harpier Team</p>
        </footer>
      </div>
    </div>
  );
}
